import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Container,
  Card,
  CardContent
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { Service } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' }
];

const ServicePublicView: React.FC = () => {
  const { qrCode } = useParams<{ qrCode: string }>();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (qrCode) {
      loadService();
    }
  }, [qrCode]);

  const loadService = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/services/public/${qrCode}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Servicio no encontrado o inactivo');
        }
        throw new Error('Error al cargar servicio');
      }

      const data = await response.json();
      setService(data);
      setError(null);
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Error al cargar servicio');
    } finally {
      setLoading(false);
    }
  };

  const getTasksForDay = (dayOfWeek: number) => {
    if (!service || !service.tasks) return [];
    return service.tasks
      .filter(t => t.dayOfWeek === dayOfWeek)
      .sort((a, b) => a.orderIndex - b.orderIndex);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Cargando...</Typography>
        </Paper>
      </Container>
    );
  }

  if (error || !service) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Servicio no encontrado'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckIcon sx={{ fontSize: 40 }} />
          <Typography variant="h4" component="h1">
            {service.name}
          </Typography>
        </Box>
        
        {service.description && (
          <Typography variant="body1" sx={{ mt: 2, opacity: 0.95 }}>
            {service.description}
          </Typography>
        )}
      </Paper>

      {/* Información del cliente */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Cliente
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {service.company ? (
            <>
              <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6">{service.company.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {service.company.address}
                </Typography>
              </Box>
            </>
          ) : service.individual ? (
            <>
              <PersonIcon color="secondary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6">{service.individual.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {service.individual.address}
                </Typography>
              </Box>
            </>
          ) : null}
        </Box>

        {service.responsibles && service.responsibles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Responsables
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {service.responsibles.map(resp => (
                <Chip
                  key={resp.id}
                  label={`${resp.user.firstName} ${resp.user.lastName}`}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Tareas por día */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tareas de la Semana
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {DAYS_OF_WEEK.map(day => {
            const tasks = getTasksForDay(day.value);
            
            return (
              <Card 
                key={day.value} 
                variant="outlined"
                sx={{
                  borderLeft: tasks.length > 0 ? '4px solid' : '1px solid',
                  borderLeftColor: tasks.length > 0 ? 'primary.main' : 'divider'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" color="primary">
                      {day.label}
                    </Typography>
                    {tasks.length > 0 && (
                      <Chip 
                        label={`${tasks.length} tarea${tasks.length !== 1 ? 's' : ''}`} 
                        size="small" 
                        color="primary"
                      />
                    )}
                  </Box>

                  {tasks.length > 0 ? (
                    <List dense>
                      {tasks.map((task, idx) => (
                        <ListItem key={task.id} disableGutters>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  color="primary" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    minWidth: 24
                                  }}
                                >
                                  {idx + 1}.
                                </Typography>
                                <Typography variant="body1">
                                  {task.taskText}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Sin tareas programadas
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Vista pública del servicio • SteamGenie
        </Typography>
      </Box>
    </Container>
  );
};

export default ServicePublicView;





